import i18n, { TFunction } from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en.json';
import fr from './fr.json';
import es from './es.json';

// Supported languages
export const SUPPORTED_LANGUAGES = ['en', 'fr', 'es'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Default language
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

// Bundled translations (shipped with the app)
const bundledResources = {
  en: { translation: en },
  fr: { translation: fr },
  es: { translation: es },
};

/**
 * Initialize i18next with bundled translations.
 * Organization-specific terminology overrides are applied separately via LocaleProvider.
 */
i18n.use(initReactI18next).init({
  resources: bundledResources,
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  interpolation: {
    escapeValue: false, // React already escapes values
  },
  react: {
    useSuspense: false, // Disable suspense to avoid loading states
  },
});

/**
 * Apply terminology overrides to a specific language.
 * Merges organization-specific terms on top of bundled defaults.
 *
 * @param languageCode - The language code (e.g., 'en', 'fr')
 * @param overrides - Dictionary of resource keys to custom values
 */
export function applyTerminologyOverrides(
  languageCode: SupportedLanguage,
  overrides: Record<string, string>
): void {
  // Convert flat key format (e.g., "entities.attribute") to nested object
  const nestedOverrides: Record<string, Record<string, string>> = {};

  Object.entries(overrides).forEach(([key, value]) => {
    const parts = key.split('.');
    if (parts.length === 2) {
      const [namespace, term] = parts;
      if (!nestedOverrides[namespace]) {
        nestedOverrides[namespace] = {};
      }
      nestedOverrides[namespace][term] = value;
    } else {
      // For flat keys without namespace, put in root
      if (!nestedOverrides['_root']) {
        nestedOverrides['_root'] = {};
      }
      nestedOverrides['_root'][key] = value;
    }
  });

  // Add each override to i18n resources
  Object.entries(nestedOverrides).forEach(([namespace, terms]) => {
    Object.entries(terms).forEach(([term, value]) => {
      const fullKey = namespace === '_root' ? term : `${namespace}.${term}`;
      i18n.addResource(languageCode, 'translation', fullKey, value);
    });
  });
}

/**
 * Change the current language.
 *
 * @param languageCode - The language code to switch to
 */
export function changeLanguage(
  languageCode: SupportedLanguage
): Promise<TFunction<'translation', undefined>> {
  return i18n.changeLanguage(languageCode) as Promise<TFunction<'translation', undefined>>;
}

/**
 * Get the current language code.
 */
export function getCurrentLanguage(): SupportedLanguage {
  return (i18n.language as SupportedLanguage) || DEFAULT_LANGUAGE;
}

/**
 * Translate a key outside of React context.
 * Use this in services, hooks, or other non-component code.
 *
 * @param key - The translation key (e.g., 'messages.saveSuccess')
 * @param options - Optional interpolation values
 * @returns The translated string
 *
 * @example
 * import { translate } from '../i18n/i18nConfig';
 * showGlobalSnackbar(translate('messages.saveSuccess'), 'success');
 */
export function translate(key: string, options?: Record<string, unknown>): string {
  return i18n.t(key, options) as string;
}

export default i18n;
